import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import type { Reservation, ReservationStatus } from "@/types/reservation";

interface UseReservationsOptions {
  mine?: boolean;
  status?: ReservationStatus[];
  from?: string;
  to?: string;
  pcId?: number;
}

interface UseReservationsResult {
  reservations: Reservation[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useReservations(options: UseReservationsOptions = {}): UseReservationsResult {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const { mine, status, from, to, pcId } = options;

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (mine) params.set("mine", "true");
    if (status && status.length > 0) params.set("status", status.join(","));
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (pcId) params.set("pcId", String(pcId));

    setLoading(true);
    setError(null);
    api
      .get<Reservation[]>(`/reservations?${params.toString()}`)
      .then(setReservations)
      .catch((err) => setError(err instanceof Error ? err.message : "読み込みに失敗しました。"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mine, JSON.stringify(status), from, to, pcId, reloadKey]);

  useEffect(() => {
    load();
  }, [load]);

  return { reservations, loading, error, reload: () => setReloadKey((k) => k + 1) };
}
